import { useEffect, useRef, useState } from 'react';
import { Button, FileInput, Progress } from '@indipia/ui-core';
import { pickPhotos, rejectNotice, type PhotoRule } from './rules';

export interface SharePhotoItem {
  key: string;
  label: string; // 예: '상차사진 1' (alt·삭제 aria-label에 사용)
  status: 'local' | 'queued' | 'uploading' | 'done' | 'failed';
  progress?: number;
  previewSrc?: string | null;
  errorText?: string;
}

export interface SharePhotoPickerProps {
  items: SharePhotoItem[];
  rule: PhotoRule;
  accept: string;
  disabled?: boolean;
  addLabel: string;
  emptyHint?: string;
  onAddFiles: (files: File[]) => void;
  onRemove: (key: string) => void;
  onRetry?: (key: string) => void;
  onOpen?: (key: string) => void;
  onNotice: (text: string) => void;
  /** 추가 버튼에 연결할 규칙 안내 요소 id (aria-describedby) */
  describedBy?: string;
}

function Thumb({ item, index, onOpen }: { item: SharePhotoItem; index: number; onOpen?: (key: string) => void }) {
  const [broken, setBroken] = useState(false);
  const showImg = !!item.previewSrc && !broken;
  const body = showImg ? (
    <img
      src={item.previewSrc as string}
      alt={item.label}
      className="h-full w-full object-cover"
      onError={() => setBroken(true)}
    />
  ) : (
    <span className="text-sm font-semibold text-text-secondary" aria-label={item.label}>
      {index + 1}
    </span>
  );
  const frame = 'flex aspect-square w-full items-center justify-center overflow-hidden rounded-xxl bg-surface-muted';
  if (item.status === 'done' && onOpen) {
    return (
      <Button
        type="button"
        variant="ghost"
        className={`${frame} p-0`}
        aria-label={`${item.label} 확대`}
        onClick={() => onOpen(item.key)}
      >
        {body}
      </Button>
    );
  }
  return <div className={frame}>{body}</div>;
}

export function SharePhotoPicker({
  items,
  rule,
  accept,
  disabled,
  addLabel,
  emptyHint,
  onAddFiles,
  onRemove,
  onRetry,
  onOpen,
  onNotice,
  describedBy,
}: SharePhotoPickerProps) {
  const full = items.length >= rule.maxCount;

  // ui-core FileInput 은 나머지 속성을 버튼이 아닌 바깥 div 로 넘기므로(수정 불가 범위),
  // 감싼 요소 안의 추가 버튼에 직접 aria-describedby 를 건다.
  const addRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const btn = addRef.current?.querySelector('button');
    if (!btn) return;
    if (describedBy) btn.setAttribute('aria-describedby', describedBy);
    else btn.removeAttribute('aria-describedby');
  }, [describedBy, disabled, full]);

  const handleFiles = (files: File[]) => {
    const { accepted, rejected } = pickPhotos(files, items.length, rule);
    const notice = rejectNotice(rejected, rule);
    if (notice) onNotice(notice);
    if (accepted.length > 0) onAddFiles(accepted);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div ref={addRef}>
          <FileInput accept={accept} multiple variant="secondary" size="lg" disabled={disabled || full} onFiles={handleFiles}>
            {addLabel}
          </FileInput>
        </div>
        <span className="text-sm text-text-secondary" aria-live="polite">
          {items.length}/{rule.maxCount}장
        </span>
      </div>

      {items.length === 0 && emptyHint && <p className="text-sm text-text-secondary">{emptyHint}</p>}

      {items.length > 0 && (
        <ul className="grid grid-cols-3 gap-2">
          {items.map((item, i) => (
            <li key={item.key} className="space-y-1">
              <Thumb item={item} index={i} onOpen={onOpen} />
              {item.status === 'uploading' && (
                <div className="space-y-1">
                  <Progress value={item.progress ?? 0} aria-label={`${item.label} 올리는 중`} />
                  <p className="text-xs text-text-secondary">올리는 중</p>
                </div>
              )}
              {item.status === 'failed' && (
                <div className="space-y-1">
                  {item.errorText && (
                    <p className="text-xs text-error" role="alert">
                      {item.errorText}
                    </p>
                  )}
                  {onRetry && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => onRetry(item.key)}>
                      다시 올리기
                    </Button>
                  )}
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`${item.label} 삭제`}
                disabled={disabled || item.status === 'uploading'}
                onClick={() => onRemove(item.key)}
              >
                삭제
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
